/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import * as GuildSoundboardActionCreators from '@app/actions/GuildSoundboardActionCreators';
import * as ModalActionCreators from '@app/actions/ModalActionCreators';
import {Input} from '@app/components/form/Input';
import * as Modal from '@app/components/modals/Modal';
import {EmojiPickerPopout} from '@app/components/popouts/EmojiPickerPopout';
import {Button} from '@app/components/uikit/button/Button';
import {Popout} from '@app/components/uikit/popout/Popout';
import {Slider} from '@app/components/uikit/Slider';
import {Logger} from '@app/lib/Logger';
import type {FlatEmoji} from '@app/types/EmojiTypes';
import {getEmojiURL} from '@app/utils/AvatarUtils';
import {
	computeWaveformPeaks,
	decodeAudioFile,
	MAX_SOUNDBOARD_DURATION_SECONDS,
	trimAudioToBase64Wav,
} from '@app/utils/SoundboardUtils';
import {SOUNDBOARD_SOUND_MAX_SIZE} from '@fluxer/constants/src/LimitConstants';
import type {GuildSoundboardSoundWithUser} from '@fluxer/schema/src/domains/guild/GuildSoundboardSchemas';
import {Trans, useLingui} from '@lingui/react/macro';
import {PauseIcon, PlayIcon, SmileyIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

const logger = new Logger('SoundboardUploadModal');

const LABEL_STYLE: React.CSSProperties = {
	fontSize: '0.75rem',
	fontWeight: 700,
	textTransform: 'uppercase',
	color: 'var(--text-primary-muted)',
};

function sanitizeName(fileName: string): string {
	const base = fileName.split('.').slice(0, -1).join('.') || fileName;
	return base.slice(0, 32).trim() || 'sound';
}

function WaveformTrimmer({
	buffer,
	trimStart,
	trimEnd,
	playheadSec,
	onChangeStart,
	onChangeEnd,
}: {
	buffer: AudioBuffer;
	trimStart: number;
	trimEnd: number;
	playheadSec: number | null;
	onChangeStart: (sec: number) => void;
	onChangeEnd: (sec: number) => void;
}) {
	const containerRef = useRef<HTMLDivElement>(null);
	const draggingRef = useRef<null | 'start' | 'end'>(null);
	const peaks = useMemo(() => computeWaveformPeaks(buffer, 96), [buffer]);
	const maxPeak = useMemo(() => Math.max(0.01, ...peaks), [peaks]);
	const duration = buffer.duration;

	useEffect(() => {
		const pctFromClientX = (clientX: number): number => {
			const el = containerRef.current;
			if (!el) return 0;
			const rect = el.getBoundingClientRect();
			return Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
		};

		const onMove = (e: PointerEvent) => {
			const which = draggingRef.current;
			if (!which) return;
			let sec = pctFromClientX(e.clientX) * duration;
			if (which === 'start') {
				sec = Math.max(0, Math.min(sec, trimEnd - 0.1));
				if (trimEnd - sec > MAX_SOUNDBOARD_DURATION_SECONDS) {
					sec = trimEnd - MAX_SOUNDBOARD_DURATION_SECONDS;
				}
				onChangeStart(sec);
			} else {
				sec = Math.min(duration, Math.max(sec, trimStart + 0.1));
				if (sec - trimStart > MAX_SOUNDBOARD_DURATION_SECONDS) {
					sec = trimStart + MAX_SOUNDBOARD_DURATION_SECONDS;
				}
				onChangeEnd(sec);
			}
		};
		const onUp = () => {
			draggingRef.current = null;
		};
		window.addEventListener('pointermove', onMove);
		window.addEventListener('pointerup', onUp);
		return () => {
			window.removeEventListener('pointermove', onMove);
			window.removeEventListener('pointerup', onUp);
		};
	}, [duration, trimStart, trimEnd, onChangeStart, onChangeEnd]);

	const startPct = (trimStart / duration) * 100;
	const endPct = (trimEnd / duration) * 100;

	return (
		<div style={{display: 'flex', flexDirection: 'column', gap: '0.25rem'}}>
			<div
				ref={containerRef}
				style={{
					position: 'relative',
					display: 'flex',
					alignItems: 'center',
					gap: '1px',
					height: '80px',
					padding: '0 0.5rem',
					borderRadius: '0.5rem',
					background: 'var(--background-secondary)',
					userSelect: 'none',
					touchAction: 'none',
				}}
			>
				{peaks.map((peak, i) => {
					const barPct = (i / peaks.length) * 100;
					const inRange = barPct >= startPct && barPct <= endPct;
					return (
						<div
							key={i}
							style={{
								flex: 1,
								height: `${Math.max(2, (peak / maxPeak) * 100)}%`,
								borderRadius: '1px',
								background: inRange ? 'var(--brand-primary, #5865f2)' : 'var(--background-header-secondary)',
							}}
						/>
					);
				})}

				<div
					style={{
						position: 'absolute',
						top: 0,
						bottom: 0,
						left: `${startPct}%`,
						width: '10px',
						marginLeft: '-5px',
						cursor: 'ew-resize',
						background: 'var(--brand-primary, #5865f2)',
						borderRadius: '3px',
					}}
					onPointerDown={(e) => {
						e.preventDefault();
						draggingRef.current = 'start';
					}}
				/>
				<div
					style={{
						position: 'absolute',
						top: 0,
						bottom: 0,
						left: `${endPct}%`,
						width: '10px',
						marginLeft: '-5px',
						cursor: 'ew-resize',
						background: 'var(--brand-primary, #5865f2)',
						borderRadius: '3px',
					}}
					onPointerDown={(e) => {
						e.preventDefault();
						draggingRef.current = 'end';
					}}
				/>

				{playheadSec != null && (
					<div
						style={{
							position: 'absolute',
							top: 0,
							bottom: 0,
							left: `${(playheadSec / duration) * 100}%`,
							width: '2px',
							marginLeft: '-1px',
							background: 'var(--white-500, #ffffff)',
							boxShadow: '0 0 3px rgba(0, 0, 0, 0.7)',
							pointerEvents: 'none',
						}}
					/>
				)}
			</div>
			<div style={{display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'var(--text-primary-muted)'}}>
				<span>{trimStart.toFixed(1)}s</span>
				<span>
					{(trimEnd - trimStart).toFixed(1)}s / {MAX_SOUNDBOARD_DURATION_SECONDS}s
				</span>
				<span>{trimEnd.toFixed(1)}s</span>
			</div>
		</div>
	);
}

interface SoundboardUploadModalProps {
	guildId: string;
	file?: File;
	existingSound?: GuildSoundboardSoundWithUser;
	existingCategories: ReadonlyArray<string>;
	onSuccess: () => void;
}

export const SoundboardUploadModal = observer(function SoundboardUploadModal({
	guildId,
	file,
	existingSound,
	existingCategories,
	onSuccess,
}: SoundboardUploadModalProps) {
	const {t} = useLingui();
	const isEdit = Boolean(existingSound);

	const [name, setName] = useState(existingSound?.name ?? (file ? sanitizeName(file.name) : ''));
	const [emojiName, setEmojiName] = useState(existingSound?.emoji_name ?? '');
	const [emojiId, setEmojiId] = useState<string | null>(existingSound?.emoji_id ?? null);
	const [volume, setVolume] = useState(existingSound?.volume ?? 1);
	const [category, setCategory] = useState(existingSound?.category ?? '');

	const [buffer, setBuffer] = useState<AudioBuffer | null>(null);
	const [trimStart, setTrimStart] = useState(0);
	const [trimEnd, setTrimEnd] = useState(0);
	const [isDecoding, setIsDecoding] = useState(Boolean(file));
	const [isProcessing, setIsProcessing] = useState(false);
	const [isPreviewing, setIsPreviewing] = useState(false);
	const [playheadSec, setPlayheadSec] = useState<number | null>(null);
	const [error, setError] = useState<string | null>(null);

	const audioContextRef = useRef<AudioContext | null>(null);
	const previewSourceRef = useRef<AudioBufferSourceNode | null>(null);
	const rafRef = useRef<number | null>(null);

	useEffect(() => {
		if (!file) return;
		let cancelled = false;
		void (async () => {
			try {
				const decoded = await decodeAudioFile(file);
				if (cancelled) return;
				setBuffer(decoded);
				setTrimStart(0);
				setTrimEnd(Math.min(decoded.duration, MAX_SOUNDBOARD_DURATION_SECONDS));
				setIsDecoding(false);
			} catch (err) {
				logger.error('Failed to decode audio file', err);
				if (!cancelled) {
					setError(t`Could not read that audio file.`);
					setIsDecoding(false);
				}
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [file, t]);

	useEffect(() => {
		return () => {
			if (rafRef.current != null) {
				cancelAnimationFrame(rafRef.current);
			}
			try {
				previewSourceRef.current?.stop();
			} catch {}
			void audioContextRef.current?.close().catch(() => {});
		};
	}, []);

	const stopPreview = useCallback(() => {
		if (rafRef.current != null) {
			cancelAnimationFrame(rafRef.current);
			rafRef.current = null;
		}
		try {
			previewSourceRef.current?.stop();
		} catch {}
		previewSourceRef.current = null;
		setPlayheadSec(null);
		setIsPreviewing(false);
	}, []);

	const togglePreview = useCallback(() => {
		if (!buffer) return;
		if (isPreviewing) {
			stopPreview();
			return;
		}
		if (!audioContextRef.current) {
			audioContextRef.current = new AudioContext();
		}
		const ctx = audioContextRef.current;
		void ctx.resume().catch(() => {});
		const source = ctx.createBufferSource();
		source.buffer = buffer;
		const gain = ctx.createGain();
		gain.gain.value = volume;
		source.connect(gain);
		gain.connect(ctx.destination);
		source.onended = () => {
			previewSourceRef.current = null;
			if (rafRef.current != null) {
				cancelAnimationFrame(rafRef.current);
				rafRef.current = null;
			}
			setPlayheadSec(null);
			setIsPreviewing(false);
		};
		const regionStart = trimStart;
		const regionEnd = trimEnd;
		const startCtxTime = ctx.currentTime;
		source.start(0, regionStart, Math.max(0.05, regionEnd - regionStart));
		previewSourceRef.current = source;
		setIsPreviewing(true);
		setPlayheadSec(regionStart);

		const tick = () => {
			const activeCtx = audioContextRef.current;
			if (!activeCtx || previewSourceRef.current !== source) {
				return;
			}
			const pos = regionStart + (activeCtx.currentTime - startCtxTime);
			if (pos >= regionEnd) {
				setPlayheadSec(null);
				rafRef.current = null;
				return;
			}
			setPlayheadSec(pos);
			rafRef.current = requestAnimationFrame(tick);
		};
		rafRef.current = requestAnimationFrame(tick);
	}, [buffer, isPreviewing, stopPreview, trimStart, trimEnd, volume]);

	const handleEmojiSelect = useCallback((emoji: FlatEmoji) => {
		if (emoji.surrogates) {
			setEmojiName(emoji.surrogates);
			setEmojiId(null);
		} else if (emoji.id) {
			setEmojiId(emoji.id);
			setEmojiName(null);
		}
	}, []);

	const clearEmoji = useCallback(() => {
		setEmojiName('');
		setEmojiId(null);
	}, []);

	const handleSubmit = useCallback(async () => {
		const trimmedName = name.trim();
		if (!trimmedName) {
			setError(t`Please enter a name.`);
			return;
		}
		setIsProcessing(true);
		setError(null);
		try {
			const emojiPayload = {
				emoji_id: emojiName ? null : emojiId,
				emoji_name: emojiName ? emojiName : null,
			};

			if (isEdit && existingSound) {
				await GuildSoundboardActionCreators.update(guildId, existingSound.id, {
					name: trimmedName,
					...emojiPayload,
					volume,
					category: category.trim() || null,
				});
			} else {
				if (!buffer) {
					setError(t`No audio to upload.`);
					setIsProcessing(false);
					return;
				}
				const {base64, byteLength} = await trimAudioToBase64Wav(buffer, trimStart, trimEnd);
				if (byteLength > SOUNDBOARD_SOUND_MAX_SIZE) {
					setError(t`That clip is too large. Try a shorter selection.`);
					setIsProcessing(false);
					return;
				}
				await GuildSoundboardActionCreators.create(guildId, {
					name: trimmedName,
					...emojiPayload,
					volume,
					category: category.trim() || null,
					sound: base64,
				});
			}
			onSuccess();
			ModalActionCreators.pop();
		} catch (err: unknown) {
			logger.error('Failed to save soundboard sound', err);
			setError(err instanceof Error ? err.message : t`Failed to save sound.`);
			setIsProcessing(false);
		}
	}, [
		name,
		emojiName,
		emojiId,
		volume,
		category,
		isEdit,
		existingSound,
		guildId,
		buffer,
		trimStart,
		trimEnd,
		onSuccess,
		t,
	]);

	return (
		<Modal.Root size="medium" centered>
			<Modal.Header title={isEdit ? t`Edit Sound` : t`Add Sound`} />
			<Modal.Content>
				<div style={{display: 'flex', flexDirection: 'column', gap: '1rem', paddingBottom: '0.5rem'}}>
					{!isEdit && (
						<div style={{display: 'flex', flexDirection: 'column', gap: '0.5rem'}}>
							<label style={LABEL_STYLE}>{t`Trim (up to 10 seconds)`}</label>
							{isDecoding && <span style={{color: 'var(--text-primary-muted)'}}>{t`Loading waveform...`}</span>}
							{buffer && (
								<>
									<WaveformTrimmer
										buffer={buffer}
										trimStart={trimStart}
										trimEnd={trimEnd}
										playheadSec={playheadSec}
										onChangeStart={setTrimStart}
										onChangeEnd={setTrimEnd}
									/>
									<Button variant="secondary" onClick={togglePreview}>
										{isPreviewing ? <PauseIcon size={16} weight="fill" /> : <PlayIcon size={16} weight="fill" />}
										<span style={{marginLeft: '0.4rem'}}>{isPreviewing ? t`Stop` : t`Preview`}</span>
									</Button>
								</>
							)}
						</div>
					)}

					<div style={{display: 'flex', gap: '0.75rem', alignItems: 'flex-end'}}>
						<div style={{flex: 1}}>
							<label style={LABEL_STYLE}>{t`Name`}</label>
							<Input type="text" value={name} maxLength={32} onChange={(e) => setName(e.target.value)} />
						</div>
						<div style={{width: '4rem', display: 'flex', flexDirection: 'column'}}>
							<label style={LABEL_STYLE}>{t`Emoji`}</label>
							<Popout
								position="bottom-end"
								render={({onClose}) => (
									<EmojiPickerPopout
										channelId={null}
										handleSelect={(emoji) => {
											handleEmojiSelect(emoji);
											onClose();
										}}
										onClose={onClose}
									/>
								)}
							>
								<button
									type="button"
									aria-label={t`Emoji`}
									onContextMenu={(e) => {
										e.preventDefault();
										clearEmoji();
									}}
									style={{
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										height: '40px',
										width: '100%',
										borderRadius: '0.25rem',
										border: '1px solid var(--background-header-secondary)',
										background: 'var(--background-secondary)',
										color: 'var(--text-primary)',
										cursor: 'pointer',
										fontSize: '1.25rem',
									}}
								>
									{emojiId ? (
										<img src={getEmojiURL({id: emojiId})} alt="" width={22} height={22} />
									) : emojiName ? (
										<span>{emojiName}</span>
									) : (
										<SmileyIcon size={20} />
									)}
								</button>
							</Popout>
						</div>
					</div>

					<div style={{display: 'flex', flexDirection: 'column', gap: '0.25rem'}}>
						<label style={LABEL_STYLE}>
							{t`Volume`}: {Math.round(volume * 100)}%
						</label>
						<Slider
							minValue={0}
							maxValue={100}
							defaultValue={Math.round(volume * 100)}
							factoryDefaultValue={100}
							onValueChange={(v) => setVolume(v / 100)}
							asValueChanges={(v) => setVolume(v / 100)}
						/>
					</div>

					<div style={{display: 'flex', flexDirection: 'column', gap: '0.4rem'}}>
						<label style={LABEL_STYLE}>{t`Category`}</label>
						<Input
							type="text"
							value={category}
							maxLength={64}
							placeholder={t`Optional`}
							onChange={(e) => setCategory(e.target.value)}
						/>
						{existingCategories.length > 0 && (
							<div style={{display: 'flex', flexWrap: 'wrap', gap: '0.4rem'}}>
								{existingCategories.map((cat) => (
									<button
										key={cat}
										type="button"
										onClick={() => setCategory(cat)}
										style={{
											border: 'none',
											cursor: 'pointer',
											borderRadius: '0.75rem',
											padding: '0.15rem 0.6rem',
											fontSize: '0.75rem',
											background: 'var(--background-tertiary)',
											color: 'var(--text-primary)',
										}}
									>
										{cat}
									</button>
								))}
							</div>
						)}
					</div>

					{error && <span style={{color: 'var(--status-danger, #d83c3e)', fontSize: '0.875rem'}}>{error}</span>}
				</div>
			</Modal.Content>
			<Modal.Footer>
				<Button variant="secondary" onClick={() => ModalActionCreators.pop()} disabled={isProcessing}>
					<Trans>Cancel</Trans>
				</Button>
				<Button
					onClick={handleSubmit}
					disabled={isProcessing || !name.trim() || (!isEdit && !buffer)}
					submitting={isProcessing}
				>
					{isEdit ? <Trans>Save</Trans> : <Trans>Create</Trans>}
				</Button>
			</Modal.Footer>
		</Modal.Root>
	);
});
